import React, { useState, useRef, useEffect } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/chat-input.css';

const ChatInput = ({
  onSendMessage,
  disabled = false,
  placeholder
}) => {
  const isMobile = useIsMobile();
  // The long form wraps to a second line on phones, where the placeholder
  // does not grow the textarea and the second line gets clipped.
  const resolvedPlaceholder = placeholder
    || (isMobile ? 'Ask Gideon anything...' : 'Ask about stocks, investments, or financial advice...');
  const [message, setMessage] = useState('');
  const [charCount, setCharCount] = useState(0);
  const textareaRef = useRef(null);

  const maxChars = 2000;

  // Quick action suggestions
  const iconProps = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const quickActions = [
    { icon: <svg {...iconProps}><path d="M5 12v8M10.5 4v16M16 9v11M21 6v14" /></svg>, text: 'Portfolio', prompt: 'Show me my portfolio summary' },
    { icon: <svg {...iconProps}><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></svg>, text: 'Markets', prompt: "What are today's market trends?" },
    { icon: <svg {...iconProps}><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 00-4 10.5c.7.6 1 1.5 1 2.5h6c0-1 .3-1.9 1-2.5A6 6 0 0012 3z" /></svg>, text: 'Ideas', prompt: 'Give me some investment ideas based on my portfolio' },
    { icon: <svg {...iconProps}><path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" /></svg>, text: 'Basics', prompt: 'Teach me the basics of investing' }
  ];

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [message]);

  // Handle text change
  const handleChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxChars) {
      setMessage(value);
      setCharCount(value.length);
    }
  };

  // Handle send
  const handleSend = () => {
    if (message.trim().length > 0 && !disabled) {
      onSendMessage(message);
      setMessage('');
      setCharCount(0);

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  // Handle key press (Enter to send, Shift+Enter for new line)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle quick action click
  const handleQuickAction = (prompt) => {
    setMessage(prompt);
    setCharCount(prompt.length);
    textareaRef.current?.focus();
  };

  const canSend = message.trim().length > 0 && !disabled;

  return (
    <div className="chat-input-container">
      {/* Quick Actions (shown when message is empty) */}
      {message.length === 0 && (
        <div className="chat-quick-actions">
          {quickActions.map((action, index) => (
            <button
              key={index}
              className="chat-quick-action"
              onClick={() => handleQuickAction(action.prompt)}
              disabled={disabled}
            >
              <span style={{ display: 'inline-flex' }}>{action.icon}</span>
              <span>{action.text}</span>
            </button>
          ))}
        </div>
      )}

      {/* Input Row */}
      <div className="chat-input-wrapper">
        {/* Text Input */}
        <textarea
          ref={textareaRef}
          className="chat-input-textarea"
          value={message}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          placeholder={disabled ? 'Please wait...' : resolvedPlaceholder}
          disabled={disabled}
          rows={1}
          aria-label="Message input"
        />

        {/* Send Button */}
        <button
          className={`chat-send-button ${canSend ? 'active' : ''}`}
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Send message"
        >
          {disabled ? '⋯' : '→'}
        </button>
      </div>

      {/* Character Counter */}
      {charCount > 1800 && (
        <div style={{
          position: 'absolute',
          bottom: '-24px',
          right: '56px',
          fontSize: '0.75rem',
          color: charCount >= maxChars ? '#ff5c5c' : 'rgba(255, 255, 255, 0.5)',
          fontWeight: '600'
        }}>
          {charCount} / {maxChars}
        </div>
      )}
    </div>
  );
};

export default ChatInput;
